import React from "react";
import { AnalyticsService } from "@shared/services/analytics/AnalyticsService";
import { formatDate } from "@shared/utils/dates/formatting";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import useRedirect from "@shared/hooks/useRedirect.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import axios from "axios";
import { PublicBaseEventSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import {
  isMultiDayEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import Icon from "@ui-kit/icons/Icon.web";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import { useRouter } from "next/router";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Box from "@ui-kit/layout/Box/Box.web";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import { PublicBaseChildEventSerializer } from "@shared/modules/events/types/public";
import StatusBadge from "@ui-application/data-display/badges/StatusBadge/StatusBadge.web";
import BackButton from "@ui-application/form/BackButton/BackButton.web";
import { getPublicPurchasePlatform } from "@shared/modules/basket/utils/basket-utils";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { useSubOptionsOptional } from "@shared/contexts/SubOptionsContext/SubOptionsContext";
import { RecurringTimeslotCardsProps } from "./RecurringTimeslotCards.props";

const notifier = NotifierFactory.createNotifier();

const RecurringTimeslotCards = ({
  eventData,
  selectedChildEvent,
  isFetchingChildEvent,
  setSelectedChildEvent,
  setIsFetchingChildEvent,
  onChildEventSelected,
  hideTitle = false,
  hideBackButton = false,
}: RecurringTimeslotCardsProps) => {
  const router = useRouter();
  const { redirect } = useRedirect();
  const { t } = useTranslation("purchases");

  // Use sub-options context for state management (optional since this component
  // might be used outside of SubOptionsProvider)
  const subOptionsContext = useSubOptionsOptional();
  const isViewingSubOptions = subOptionsContext?.isViewingSubOptions || false;

  const getStartEndLabels = (
    childEvent: PublicBaseEventSerializer | PublicBaseChildEventSerializer
  ) => {
    const isMultiDayOrTBD =
      childEvent?.date_time_to_be_determined || isMultiDayEvent(eventData);

    const getTBDOrFormattedDate = (
      date: string | undefined,
      format: string,
      options?: { displayTime?: boolean; displayTimezone?: boolean }
    ): string => {
      if (childEvent?.date_time_to_be_determined) {
        return t("Date and Time TBD");
      }
      return formatDate({
        date,
        timezone: childEvent?.timezone,
        format,
        displayTime: options?.displayTime,
        displayTimezone: options?.displayTimezone,
      });
    };

    return (
      <Flex direction="column" width="100%" minWidth={0}>
        <Text
          hidden={!!selectedChildEvent}
          fontWeight="bold"
          mb="xs"
          whiteSpace="normal"
          wordBreak="break-word"
          overflowWrap="break-word"
        >
          {childEvent?.name}
        </Text>
        {isMultiDayOrTBD ? (
          <SimpleGrid
            fontSize="md"
            width="100%"
            columns={{ base: 1, lg: 2 }}
            gap="xs"
          >
            <Flex direction="column">
              <Text color="gray.600">{t("Starts")}</Text>
              <Text fontWeight="normal">
                {getTBDOrFormattedDate(
                  childEvent?.starts_on,
                  "EEEE MMM d, yyyy |",
                  { displayTime: true, displayTimezone: true }
                )}
              </Text>
            </Flex>
            <Flex direction="column">
              <Text color="gray.600">{t("Ends")}</Text>
              <Text fontWeight="normal">
                {getTBDOrFormattedDate(
                  childEvent?.ends_on,
                  "EEEE MMM d, yyyy |",
                  { displayTime: true, displayTimezone: true }
                )}
              </Text>
            </Flex>
          </SimpleGrid>
        ) : (
          <Flex fontSize="md" width="100%" direction="column">
            <Text fontWeight="normal" color="gray.600">
              {formatDate({
                date: childEvent?.starts_on,
                timezone: childEvent?.timezone,
                format: "EEEE MMM d, yyyy",
                displayTime: false,
              })}
            </Text>
            <Text fontWeight="normal">
              {formatDate({
                date: childEvent?.starts_on,
                timezone: childEvent?.timezone,
                format: "h:mm a",
              })}
              {" - "}
              {formatDate({
                date: childEvent?.ends_on,
                timezone: childEvent?.timezone,
                format: "h:mm a zzz",
              })}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  };

  const getChildEventInfo = async (
    childEvent: PublicBaseChildEventSerializer
  ) => {
    const childEventParams = {
      purchase_platform: getEmbeddedPurchasePlatform(),
      payment_type: [
        PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
        PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
        PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
      ].toString(),
      source_channel: router?.query?.source_channel?.toString(),
    };

    // if sold out, do nothing
    if (isSoldOutEvent(childEvent) && !childEvent.is_waitlisted) {
      return;
    }

    if (onChildEventSelected?.(childEvent)) {
      return;
    }

    // if the child has assigned seating, redirect to the seating map page
    if (childEvent.assigned_space) {
      redirect(`/${childEvent.slug}/seating/`);
      return;
    }

    setIsFetchingChildEvent(true);

    try {
      const eventRepository = new PublicBaseEventRepository();
      if (childEvent?.slug) {
        const eventData = await eventRepository.retrieve(childEvent.slug, {
          params: childEventParams,
        });
        if (eventData && eventData.venue) {
          setSelectedChildEvent(eventData);
          AnalyticsService.viewContent({
            label: enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id,
            event_id: eventData.id,
            venue_id: eventData.venue?.id,
            custom_params: {
              purchase_source_platform: getPublicPurchasePlatform(),
            },
            capi_should_trigger: true,
          });
          AnalyticsService.viewTicketTypes(eventData, eventData.venue);
        }
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        notifier.error(
          getExceptionToastDescription(error) || t("An error occurred")
        );
      } else {
        notifier.error(t("An unexpected error occurred"));
      }
    } finally {
      setIsFetchingChildEvent(false);
    }
  };

  return (
    <Flex direction="column" data-testid="recurring-timeslot-modal">
      {!selectedChildEvent ? (
        <Flex
          alignItems="center"
          w="100%"
          direction="column"
          hidden={isFetchingChildEvent || hideTitle}
        >
          <Text fontWeight="bold" fontSize="xl" mb="lg">
            {isMultiDayEvent(eventData)
              ? t("Select a Date")
              : t("Select a Time")}
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" hidden={isViewingSubOptions}>
          {!hideBackButton && (
            <BackButton
              data-testid="recurring-timeslot-cards-back"
              onClick={() => setSelectedChildEvent(undefined)}
            />
          )}
          <Flex pt="md" px={{ base: "lg", sm: "none" }}>
            {getStartEndLabels(selectedChildEvent)}
          </Flex>
        </Flex>
      )}
      {!selectedChildEvent && !isFetchingChildEvent && (
        <Flex direction="column" gap="md">
          {(eventData?.child_events as PublicBaseChildEventSerializer[])?.map(
            (childEvent) => (
              <Button
                data-testid="child-event-card"
                key={childEvent.id}
                w="full"
                h="fit-content"
                bg="bg"
                color="font.dark"
                rounded={{ base: "sm", sm: "lg" }}
                py={{ base: "md", sm: "lg" }}
                px="lg"
                _active={{
                  backgroundColor: { _light: "bg", _dark: "bg.subtle" },
                }}
                _hover={{
                  backgroundColor: { _light: "bg", _dark: "bg.subtle" },
                }}
                _disabled={{
                  backgroundColor: { _light: "bg", _dark: "bg.subtle" },
                  opacity: "0.4",
                  cursor: "not-allowed",
                }}
                isDisabled={
                  isSoldOutEvent(childEvent) && !childEvent.is_waitlisted
                }
                onClick={() => getChildEventInfo(childEvent)}
              >
                <Flex
                  width="100%"
                  minWidth={0}
                  overflow="hidden"
                  textAlign="left"
                  direction="column"
                >
                  {eventData.venue && (
                    <Box mb="xs">
                      <InventoryBadge
                        eventData={childEvent}
                        venueData={eventData.venue}
                      />
                    </Box>
                  )}
                  {isSoldOutEvent(childEvent) && !childEvent.is_waitlisted && (
                    <Box mb="xs">
                      <StatusBadge status="error">
                        <Text fontSize="xs" textTransform="uppercase">
                          {t("Sold Out")}
                        </Text>
                      </StatusBadge>
                    </Box>
                  )}
                  <Flex alignItems="center" minWidth={0}>
                    {getStartEndLabels(childEvent)}
                    <Icon
                      name="ChevronRight"
                      boxSize="element.xl"
                      flexShrink={0}
                    />
                  </Flex>
                </Flex>
              </Button>
            )
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default RecurringTimeslotCards;
